/**
 * Single source of truth for "active" vs "inactive" listing status.
 * Used by MyListings, ProductDetail, and any other UI that shows listing status
 * so the backend notion of "published and not expired" is consistent everywhere.
 */

export type ListingDisplayStatus = "active" | "inactive" | "draft";

/** Minimal product shape needed to compute display status (backend can send status/expiresAt in various formats). */
export interface ProductForStatus {
  status?: string | null;
  expiresAt?: string | Date | null;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Number of days until expiry; positive = in future, 0 or negative = expired or today. */
function getDaysUntilExpiry(expiresAt: string | Date | null | undefined): number {
  if (expiresAt == null) return NaN;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return ms / MS_PER_DAY;
}

/**
 * Derive display status and optional "days left" from backend product.
 * - Active: status is PUBLISHED (case-insensitive) and (no expiresAt or expiresAt in the future).
 * - Draft: status is DRAFT (case-insensitive).
 * - Inactive: otherwise (SOLD, EXPIRED, or PUBLISHED but expired).
 * - daysLeft: only set when 0 < daysLeft <= 3 (for "X days left" badge); undefined otherwise.
 */
export function getListingDisplayStatus(
  product: ProductForStatus | null | undefined
): { status: ListingDisplayStatus; daysLeft: number | undefined } {
  const status = product?.status != null ? String(product.status).toUpperCase() : "";
  
  if (status === "DRAFT") {
    return { status: "draft", daysLeft: undefined };
  }

  if (status !== "PUBLISHED") {
    return { status: "inactive", daysLeft: undefined };
  }

  const expiresAt = product?.expiresAt;
  if (expiresAt == null || expiresAt === "") {
    return { status: "active", daysLeft: undefined };
  }

  const rawDays = getDaysUntilExpiry(expiresAt);
  const daysLeft = Math.ceil(rawDays);
  if (daysLeft <= 0) {
    return { status: "inactive", daysLeft: undefined };
  }

  return {
    status: "active",
    daysLeft: daysLeft <= 3 ? daysLeft : undefined,
  };
}
